import type {
  NameType,
  Payload,
  ValueType,
} from 'recharts/types/component/DefaultTooltipContent';
import type { AxisDomain } from 'recharts/types/util/types';

import { type ChartColor } from './chartColors';
import {
  type ChartDatum,
  type ChartValueFormatter,
  type IChartTooltipEntry,
  PRELIMINARY_KEY,
} from './chartTypes';

export const DEFAULT_VALUE_FORMATTER: ChartValueFormatter = (value) =>
  value.toString();

export const CHART_FONT_SIZE = 12;
export const AXIS_TICK = {
  className: 'fill-text-tertiary',
  fontSize: CHART_FONT_SIZE,
};
export const GRID_CLASS = 'stroke-border-secondary stroke-1';
export const PRELIMINARY_DASH = '4 4';
export const PRELIMINARY_BAR_OPACITY = 0.4;
export const BOUNDARY_LABEL = 'Books closed';
export const BOUNDARY_LINE_CLASS = 'stroke-border-primary';
export const BOUNDARY_LABEL_PROPS = {
  value: BOUNDARY_LABEL,
  position: 'insideTopLeft',
  className: 'fill-text-tertiary',
  fontSize: CHART_FONT_SIZE,
} as const;

const PRELIMINARY_SUFFIX = '__preliminary';

/** Intermediate x-axis labels drawn between the first and last when labels are sparse. */
const MAX_INNER_TICKS = 4;

/** Series added by `splitPreliminary`: closed months plus, for continuity, the last closed point. */
export interface IPreliminarySplit {
  data: ChartDatum[];
  /** Dashed companion series keyed by original category. */
  preliminaryKeys: Map<string, string>;
  /** x-axis label of the last closed month, where the boundary line sits. */
  boundaryLabel: string | null;
}

/** Shared plumbing for the Recharts-based chart primitives. */
export class ChartHelper {
  static yAxisDomain(
    autoMinValue: boolean,
    minValue: number | undefined,
    maxValue: number | undefined
  ): AxisDomain {
    return [autoMinValue ? 'auto' : (minValue ?? 0), maxValue ?? 'auto'];
  }

  /** First, last and at most four evenly spaced x values in between. */
  static sparseTicks(data: ChartDatum[], index: string): string[] {
    const labels = data.map((d) => String(d[index] ?? ''));
    const last = labels.length - 1;
    if (last < 1) return labels;
    const inner = Math.min(MAX_INNER_TICKS, last - 1);
    const positions = new Set<number>([0, last]);
    for (let i = 1; i <= inner; i += 1) {
      positions.add(Math.round((i * last) / (inner + 1)));
    }
    return [...positions].sort((a, b) => a - b).map((p) => labels[p] ?? '');
  }

  /** Converts Recharts' tooltip payload into typed, colour-resolved entries. */
  static tooltipEntries(
    payload: Payload<ValueType, NameType>[] | undefined,
    colors: Map<string, ChartColor>
  ): IChartTooltipEntry[] {
    const seen = new Set<string>();
    return (payload ?? []).flatMap<IChartTooltipEntry>((item) => {
      const category =
        typeof item.name === 'string'
          ? item.name.replace(PRELIMINARY_SUFFIX, '')
          : undefined;
      if (category !== undefined && seen.has(category)) return [];
      if (category !== undefined) seen.add(category);
      const value = typeof item.value === 'number' ? item.value : undefined;
      const color = category === undefined ? undefined : colors.get(category);
      if (category === undefined || value === undefined) return [];
      if (!color || color === 'transparent') return [];
      return [{ category, value, color }];
    });
  }

  static isPreliminary(datum: unknown): boolean {
    return (
      typeof datum === 'object' &&
      datum !== null &&
      PRELIMINARY_KEY in datum &&
      (datum as Record<typeof PRELIMINARY_KEY, unknown>)[PRELIMINARY_KEY] ===
        true
    );
  }

  /** Whether the hovered datum (first payload item) is a preliminary month. */
  static tooltipPreliminary(
    payload: Payload<ValueType, NameType>[] | undefined
  ): boolean {
    return ChartHelper.isPreliminary(payload?.[0]?.payload);
  }

  /** x-axis label of the last closed month, or null when no open months follow it. */
  static boundaryLabel(data: ChartDatum[], index: string): string | null {
    const firstOpen = data.findIndex((d) => ChartHelper.isPreliminary(d));
    if (firstOpen <= 0) return null;
    const boundary = data[firstOpen - 1]?.[index];
    return typeof boundary === 'string' ? boundary : null;
  }

  /**
   * Recharts draws one dash pattern per series, so open months move into a
   * dashed companion series that starts at the last closed point.
   */
  static splitPreliminary(
    data: ChartDatum[],
    index: string,
    categories: string[]
  ): IPreliminarySplit {
    const firstOpen = data.findIndex((d) => ChartHelper.isPreliminary(d));
    if (firstOpen <= 0) {
      return { data, preliminaryKeys: new Map(), boundaryLabel: null };
    }
    const preliminaryKeys = new Map(
      categories.map((c) => [c, `${c}${PRELIMINARY_SUFFIX}`])
    );
    const split = data.map((datum, i) => {
      if (i < firstOpen - 1) return datum;
      const copy: ChartDatum = { ...datum };
      categories.forEach((c) => {
        copy[preliminaryKeys.get(c) ?? c] = datum[c];
        if (i >= firstOpen) copy[c] = null;
      });
      return copy;
    });
    return {
      data: split,
      preliminaryKeys,
      boundaryLabel: ChartHelper.boundaryLabel(data, index),
    };
  }

  static tooltipLabel(label: unknown): string {
    return typeof label === 'string' || typeof label === 'number'
      ? String(label)
      : '';
  }
}
